/**
 * LlmModal - Modal fullscreen pour les deep links LLM
 * RM-140: Ouvre les pages en mode modal avec X à gauche pour retourner sur /dashboard/llm
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../../core/design-system/components/Icon';
import './styles.css';

/**
 * Modal fullscreen pour le LLM
 * @param {Object} props
 * @param {boolean} props.isOpen - Si la modal est ouverte
 * @param {Function} props.onClose - Callback pour fermer
 * @param {string} props.title - Titre de la modal
 * @param {React.ReactNode} props.children - Contenu
 * @param {boolean} props.showBackButton - Afficher bouton retour au lieu de X
 * @param {Function} props.onBack - Callback pour retour arrière
 */
const LlmModal = ({ isOpen, onClose, title, children, showBackButton, onBack }) => {
  const { t } = useTranslation('common');
  
  // Bloquer le scroll du body quand ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Fermer avec Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Aria-label avec fallback
  const dialogAriaLabel = title && title.trim().length > 0 ? title : t('dialog', 'Dialog');

  // Utiliser un portal pour s'afficher au-dessus de tout (y compris le topbar)
  return createPortal(
    <div className="llm-modal-overlay" role="dialog" aria-modal="true" aria-label={dialogAriaLabel}>
      <div className="llm-modal">
        {/* Header avec bouton retour ou X à gauche */}
        <div className="llm-modal-header">
          {showBackButton ? (
            <button
              type="button"
              className="llm-modal-back"
              onClick={onBack}
              aria-label={t('back', 'Back')}
            >
              <Icon name="arrow-left" size={24} />
            </button>
          ) : (
            <button
              type="button"
              className="llm-modal-close"
              onClick={onClose}
              aria-label={t('closeAndReturn', 'Close and return')}
            >
              <Icon name="close" size={24} />
            </button>
          )}
          {title && <h2 className="llm-modal-title">{title}</h2>}
          {/* Bouton X à droite quand on a le bouton retour */}
          {showBackButton && (
            <button
              type="button"
              className="llm-modal-close llm-modal-close-right"
              onClick={onClose}
              aria-label={t('close', 'Close')}
            >
              <Icon name="close" size={20} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="llm-modal-content">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LlmModal;
